/* eslint-disable no-console */
import { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import {
  registerServiceWorker,
  setUpdatePromptCallback,
  skipWaiting,
  checkForUpdate,
  getServiceWorkerVersion,
} from '../utils/serviceWorker'
import { ServiceWorkerContext } from './ServiceWorkerContextDef'

// Configuration for periodic update checking
const UPDATE_CHECK_INTERVAL = 5 * 60 * 1000 // Check every 5 minutes (production)
const UPDATE_CHECK_INTERVAL_DEV = 30 * 1000 // Check every 30 seconds (development)
const isDevelopment = import.meta.env.DEV

export const ServiceWorkerProvider = ({ children }) => {
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [serviceWorkerVersion, setServiceWorkerVersion] = useState(null)
  const [waitingWorker, setWaitingWorker] = useState(null)

  // Initialize service worker
  useEffect(() => {
    const initServiceWorker = async () => {
      // Set up update callback before registration
      setUpdatePromptCallback((worker) => {
        console.log('[SW Context] Update available')
        setWaitingWorker(worker)
        setIsUpdateAvailable(true)
      })

      // Register service worker
      const registration = await registerServiceWorker()

      if (registration) {
        // Get initial version
        try {
          const version = await getServiceWorkerVersion()
          setServiceWorkerVersion(version)
          console.log('[SW Context] Service Worker version:', version)
        } catch (error) {
          console.error('[SW Context] Failed to get version:', error)
        }
      }
    }

    initServiceWorker()
  }, [])

  // Update service worker
  const updateServiceWorker = useCallback(async () => {
    if (!waitingWorker) {
      console.log('[SW Context] No waiting worker to update')
      return false
    }

    setIsUpdating(true)
    console.log('[SW Context] Starting update...')

    try {
      // Skip waiting and reload
      const success = await skipWaiting()
      if (success) {
        console.log('[SW Context] Update successful, reloading...')
        // Page will reload automatically from skipWaiting
      } else {
        console.error('[SW Context] Update failed')
        setIsUpdating(false)
      }
      return success
    } catch (error) {
      console.error('[SW Context] Update error:', error)
      setIsUpdating(false)
      return false
    }
  }, [waitingWorker])

  // Check for updates manually
  const checkForServiceWorkerUpdate = useCallback(async () => {
    console.log('[SW Context] Checking for updates...')
    const hasUpdate = await checkForUpdate()
    if (hasUpdate) {
      console.log('[SW Context] Update found')
      setIsUpdateAvailable(true)
    } else {
      console.log('[SW Context] No updates available')
    }
    return hasUpdate
  }, [])

  // Periodic update checking
  useEffect(() => {
    // Don't check if update is already available or currently updating
    if (isUpdateAvailable || isUpdating) {
      return
    }

    const checkInterval = isDevelopment ? UPDATE_CHECK_INTERVAL_DEV : UPDATE_CHECK_INTERVAL

    console.log(
      `[SW Context] Setting up periodic update check every ${checkInterval / 1000} seconds`
    )

    // Check immediately on mount (after a short delay to let SW initialize)
    const initialCheckTimeout = setTimeout(() => {
      checkForServiceWorkerUpdate()
    }, 5000) // Wait 5 seconds after mount for initial check

    // Set up periodic checking
    const intervalId = setInterval(() => {
      console.log('[SW Context] Periodic update check...')
      checkForServiceWorkerUpdate()
    }, checkInterval)

    // Also check when the page becomes visible (user returns to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden && !isUpdateAvailable && !isUpdating) {
        console.log('[SW Context] Page became visible, checking for updates...')
        checkForServiceWorkerUpdate()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup
    return () => {
      clearTimeout(initialCheckTimeout)
      clearInterval(intervalId)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkForServiceWorkerUpdate, isUpdateAvailable, isUpdating])

  // Listen for controller change (happens after skipWaiting)
  useEffect(() => {
    const handleControllerChange = () => {
      console.log('[SW Context] Controller changed, getting new version')
      // Get new version after update
      getServiceWorkerVersion()
        .then((version) => {
          setServiceWorkerVersion(version)
          console.log('[SW Context] New version:', version)
        })
        .catch((error) => {
          console.error('[SW Context] Failed to get new version:', error)
        })
    }

    navigator.serviceWorker?.addEventListener('controllerchange', handleControllerChange)

    return () => {
      navigator.serviceWorker?.removeEventListener('controllerchange', handleControllerChange)
    }
  }, [])

  const value = {
    isUpdateAvailable,
    isUpdating,
    serviceWorkerVersion,
    updateServiceWorker,
    checkForServiceWorkerUpdate,
  }

  return <ServiceWorkerContext.Provider value={value}>{children}</ServiceWorkerContext.Provider>
}

ServiceWorkerProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
